import { SidePanelContent } from './SidePanelContent'
import { SidePanelFooter } from './SidePanelFooter'
import { SidePanelHeader } from './SidePanelHeader'

import type { FC } from 'react'
import type { SidePanelContentProps } from './SidePanelContent'
import type { SidePanelFooterProps } from './SidePanelFooter'
import type { SidePanelHeaderProps } from './SidePanelHeader'

interface SidePanelProps {
	header: SidePanelHeaderProps
	children: SidePanelContentProps['children']
	footer?: SidePanelFooterProps
}

/**
 * Navigation component that contains a header, main content area, and optional footer
 *
 * @param header - an object containing the header props:
 * @param header.title - the title to display in the header
 * @param header.onAdd - an optional callback function to handle the click event on the add button
 * @param header.backEnabled - an optional boolean to display the back button
 *
 * @param children - the content to display in the main content area
 *
 * @param footer - an optional object containing the footer props:
 * @param footer.buttonLabel - the label to display on the footer button
 * @param footer.icon - an optional icon to display on the left side of the footer button
 * @param footer.handler - the callback function to handle the click event on the footer button
 *
 * @example
 * <SidePanel
 *     header={{ title: "Header", onAdd: () => {}, backEnabled: true }}
 *     footer={{ buttonLabel: "Button", icon: <svg />, handler: () => {} }}>
 *     <Box>Example content</Box>
 * </SidePanel>
 */
export const SidePanel: FC<SidePanelProps> = ({ header, children, footer }) => {
	return (
		<div className="w-[320px] h-full flex flex-col overflow-hidden" data-testid="SidePanel">
			<SidePanelHeader {...header} />
			<div className="flex-1 overflow-y-auto border-r">
				<SidePanelContent>{children}</SidePanelContent>
			</div>
			{footer && <SidePanelFooter {...footer} />}
		</div>
	)
}
